//Get yotpo top reviews for product review schema.org

export const YOTPO_APP_KEY = 'mPsE4vXKaq2SbFLNe97Us9Gj3Mn5jQyH85l461fT';

const REVIEWS_API_BASE = 'https://api-cdn.yotpo.com/v1/widget';

const REQUEST_TIMEOUT_MS = 1000;

const PAGE_SIZE = 20;

export type TopReview = {
    rating: number;
    title?: string;
    content: string;
    author: string;
    createdAt?: string;
};

type YotpoReview = {
    score?: number;
    title?: string;
    content?: string;
    created_at?: string;
    user?: { display_name?: string };
};

type YotpoReviewsResponse = {
    response?: { reviews?: YotpoReview[] };
};

const toPlainText = (value?: string) =>
    (value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;|&apos;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\s+/g, ' ')
        .trim();

/**
 * A review is only usable if it has a rating, a body and an author - schema.org
 * `Review` requires all three, so anything short of that is dropped rather than
 * put a placeholder.
 */
const mapReview = (review: YotpoReview): TopReview | null => {
    const rating = Number(review.score);
    const content = toPlainText(review.content);
    const author = toPlainText(review.user?.display_name);
    const title = toPlainText(review.title);

    if (!Number.isFinite(rating) || rating <= 0 || !content || !author) return null;

    return {
        rating,
        ...(title ? { title } : {}),
        content,
        author,
        ...(review.created_at ? { createdAt: review.created_at } : {}),
    };
};

type FetchTopReviewsArgs = {
    productId?: string;
    // `c_yotpoReviewCount`. Zero or missing means there is nothing to fetch.
    reviewCount?: number;
};

/**
 * Never throws. Any failure - timeout, blocked egress, unexpected payload -
 * resolves to an empty list, and the PDP renders without Review markup.
 *
 * Products with no reviews skip the request entirely, which is most of the
 * catalogue.
 */
export const fetchTopReviews = async ({
    productId,
    reviewCount,
}: FetchTopReviewsArgs): Promise<TopReview[]> => {
    if (!productId || reviewCount === 0) return [];

    try {
        const url = `${REVIEWS_API_BASE}/${YOTPO_APP_KEY}/products/${encodeURIComponent(
            productId,
        )}/reviews.json?per_page=${PAGE_SIZE}`;

        const response = await fetch(url, { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) });

        if (!response.ok) return [];

        const body = (await response.json()) as YotpoReviewsResponse;
        const reviews = body.response?.reviews;

        if (!Array.isArray(reviews)) return [];

        return reviews.flatMap((review) => mapReview(review) ?? []);
    } catch {
        return [];
    }
};
